import { Injectable } from '@angular/core';
import { PlatformLocation } from '@angular/common';
import {
    StateService,
    StateDeclaration,
    TransitionOptions,
    RawParams,
    UrlService,
    UrlParts,
    StateRule,
} from '@uirouter/angular';
import { FiStateChangeService } from './fi-state-change.service';
import { Subject } from 'rxjs';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';

interface IReturnToStateItem {
    fromState: string;
    toState: string;
    toStateParams: RawParams;
}

interface IStateStackItem {
    stateName: string;
    stateParams: RawParams;
    currentStateName: string;
}

@Injectable()
export class FiStateService {
    static isFirstRun = true;

    isHistoryBack = false;
    private _onFiStateGo$ = new Subject<{ name: string; params: RawParams }>();
    private statesStack: IStateStackItem[] = [];
    get onFiStateGo$() {
        return this._onFiStateGo$.asObservable();
    }

    constructor(
        location: PlatformLocation,
        private uiStateService: StateService,
        private fiStateChangeService: FiStateChangeService,
        private urlService: UrlService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
    ) {
        let popStateFired = false;

        if (FiStateService.isFirstRun) {
            setTimeout(() => {
                location.onPopState(() => {
                    popStateFired = true;
                    this.isHistoryBack = true;
                });
                this.fiStateChangeService.onSuccess$.subscribe((tr) => {
                    if (popStateFired) {
                        setTimeout(() => {
                            popStateFired = false;
                        });
                    } else {
                        this.isHistoryBack = false;
                    }
                    this.previousStateName = this.currentStateName;
                    this.previousStateParams = this.currentStateParams;
                    this.currentStateName = tr.to().name;
                    this.currentStateParams = tr.params();
                });
            });
            FiStateService.isFirstRun = false;
        }

        setTimeout(() => {
            const data = this.uiStateService
                .get()
                .filter((stateDeclaration) => !stateDeclaration.abstract)
                .map((stateDeclaration) => {
                    return {
                        name: stateDeclaration.name,
                        url: stateDeclaration.url,
                        paretn: stateDeclaration.parent,
                    };
                });
        });
    }

    go(to: string, params?: RawParams, options?: TransitionOptions) {
        this._onFiStateGo$.next({
            name: to,
            params: params,
        });
        return this.uiStateService.go(this.normalizeStateName(to), params, options);
    }

    transitionTo(to: string, params?: RawParams, options?: TransitionOptions) {
        this.uiStateService.transitionTo(this.normalizeStateName(to), params, options);
    }

    href(
        stateName: string,
        stateParams?: RawParams,
        options?: {
            inherit?: boolean;
            reload?: boolean;
        },
    ) {
        return this.uiStateService.href(this.normalizeStateName(stateName), stateParams, options);
    }

    get current(): StateDeclaration {
        return this.uiStateService.current;
    }

    get params(): any {
        return this.uiStateService.params;
    }

    getParams(transition): RawParams {
        return transition.params();
    }

    setBackButtonHandler(callback: () => void) {
        ((onpopstate) => {
            window.onpopstate = () => {
                callback();
                window.onpopstate = onpopstate;
            };
        })(window.onpopstate);
    }

    private currentStateName: string;
    private currentStateParams: RawParams;
    private previousStateName: string;
    private previousStateParams: RawParams;
    getCurrentStateName(): string {
        return this.currentStateName;
    }
    getCurrentStateParams(): RawParams {
        return this.currentStateParams;
    }
    getPreviousStateName(): string {
        return this.previousStateName;
    }
    getPreviousStateParams(): RawParams {
        return this.previousStateParams;
    }

    private normalizeStateName(stateName: string) {
        // previous mobile app versions support
        if (stateName === 'DocumentModule.Create') return FiStateNames.Document.Add;
        if (stateName === 'DocumentModule.View') return FiStateNames.Document.View;
        if (stateName === 'ModuleMediaBankView') return FiStateNames.MediaBank.View;
        if (stateName === 'PostView') return FiStateNames.PostView;
        if (stateName === 'Module.NewForum.View') return FiStateNames.Forum.View;

        return stateName;
    }

    getStateByUrl(href: string) {
        try {
            const urlObj = new URL(href);
            const searchParams = {};
            urlObj.searchParams.forEach((value: string, key: string) => {
                searchParams[key] = value;
            });
            const urlParts: UrlParts = { path: urlObj.pathname, search: searchParams, hash: urlObj.hash };
            const best = this.urlService.match(urlParts);

            if (best && best.rule && best.rule.type === 'STATE') {
                const stateRule = best.rule as StateRule;
                if (stateRule && stateRule.state.name) {
                    return {
                        stateName: stateRule.state.name,
                        stateParams: best.match,
                    };
                }
            }
        } catch (ex) {
            setTimeout(() => {
                this.fiDialogsService.error({
                    headerText: this.appTextService.get('error_message.invalid_url_header'),
                    messageText: this.appTextService.get('error_message.invalid_url_text'),
                });
            });
        }
    }

    private returnToSateData: IReturnToStateItem[] = [];
    registerReturnToCurrentState(fromState: string) {
        this.registerReturnToState({
            fromState: fromState,
            toState: this.getCurrentStateName(),
            toStateParams: this.getCurrentStateParams(),
        });
    }
    registerReturnToState(returnToState: IReturnToStateItem) {
        const idx = this.returnToSateData.findIndex((item) => item.fromState === returnToState.fromState);
        if (idx !== -1) {
            this.returnToSateData[idx] = returnToState;
        } else {
            this.returnToSateData.push(returnToState);
        }
    }
    checkReturnToState(fallbackFn: () => void) {
        const returnToStateItem = this.returnToSateData.find((item) => item.fromState === this.current.name);
        if (returnToStateItem) {
            this.returnToSateData.splice(this.returnToSateData.indexOf(returnToStateItem), 1);
            this.go(returnToStateItem.toState, returnToStateItem.toStateParams);
        } else {
            // no registered state for returning from current state
            fallbackFn();
        }
    }
}
